import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { CommonService } from '../../../../../../../../../../shared/services/common.service';
import { Subject } from 'rxjs';
import { DailyReportService } from '../../../../daily-report.service';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../../../../../../../../../state/app.state';
import { dailyReport, projectDetails } from '../../../../../../../../core/projects.selectors';
import { ProjectsService } from '../../../../../../../../core/projects.service';
import { DeleteVideoFromMuxDbRequest } from '../../../../../../../../core/projects.actions';
import { translations } from '../../../../../../../../../../shared/core/shared.selectors';
import { Ability, subject } from '@casl/ability';
import {
  clearLocationControls,
  linkRawValue,
  patchPickedLocation,
  recordLinkPayload,
} from '../../../../../../../../../../shared/helpers/design-context';

@Component({
    selector: 'app-activity-item-details',
    templateUrl: './activity-item-details.component.html',
    styleUrls: ['./activity-item-details.component.scss', '../../input-component-styles.scss'],
    standalone: false
})
export class ActivityItemDetailsComponent implements OnInit, OnDestroy {
  @Input() data: any;
  @Input() options: any = {
    showActionsSection: true,
  };
  errorMessages: any = {};
  errorMessageMap = {
    type: {
      required: 'This field is required.',
    },
  };
  mediaFiles = [];
  selectedLineItem;
  form: FormGroup = new FormGroup({
    title: new FormControl('', [Validators.required]),
    type: new FormControl(''),
    latitude: new FormControl(''),
    longitude: new FormControl(''),
    location: new FormControl(''),
    station_range: new FormControl(null),
    pictures: new FormControl('[]'),
    videos: new FormControl('[]'),
    map_feature_link: new FormControl(null),
  });
  disableOptionMenu = false;
  videoToBeDeletedFromMuxDb = [];
  activityTypeOptions: any;
  translations: any;
  displayActivityType = true;
  dailyReport: any;
  editable: any;
  private formSubscription: Subscription;
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private commonService: CommonService,
    private projectsService: ProjectsService,
    private dailyReportService: DailyReportService,
    private store: Store<fromRoot.State>,
    private cdRef: ChangeDetectorRef,
    private ability: Ability,
  ) {

    this.store.select(projectDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        this.activityTypeOptions = data.activity_types;
      });

    this.store.select(translations)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data: any) => {
        this.translations = data;
      });
  }

  ngOnInit() {
    if (this.options.disabled) {
      this.form.disable({ emitEvent: false });
    }

    if (this.data) {
      this.form.patchValue({
        title: this.data?.title,
        latitude: this.data?.latitude,
        longitude: this.data?.longitude,
        location: this.data?.location,
        station_range: this.data?.station_range,
        type: this.data?.type,
        pictures: JSON.stringify(this.data?.pictures),
        videos: JSON.stringify(this.data?.videos),
        map_feature_link: this.data?.map_feature_link ?? null,
      }, { emitEvent: false });

      this.displayActivityType = this.data.type;

      this.mediaFiles = this.data?.media;
    } else {
      this.disableOptionMenu = true;
      const newActivity = {
        id: `new_${new Date().getTime()}`,
        title: '',
        description: '',
        latitude: '',
        longitude: '',
        location: '',
        station_range: null,
        type: '',
        pictures: [],
        videos: [],
        address: null,
        annotation: null,
        map_feature_link: null,
      };

      this.store.select(dailyReport)
        .subscribe((res: any) => {
          const index = res.activities?.length || 0;

          this.data = {
            index: index + 1,
            ...newActivity,
          };

          this.cdRef.detectChanges();
        });
    }

    this.commonService.uploadedFilesInfo
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        const items = Array.isArray(data) ? data : [data];

        items.forEach((eachItem: any) => {
          if (eachItem?.['fileType'] === 'picture' || eachItem?.['fileType'] === 'document') {
            let pictures = this.form.get('pictures')?.value || '[]';
            pictures = JSON.parse(pictures);
            pictures.push(eachItem);
            this.form?.get('pictures')?.setValue(JSON.stringify(pictures));
            this.mediaFiles.push(eachItem);  // Add to mediaFiles array

          } else if (eachItem?.['fileType'] === 'video') {
            let videos = this.form.get('videos')?.value || '[]';
            videos = JSON.parse(videos);

            videos.push(eachItem);
            this.form?.get('videos')?.setValue(JSON.stringify(videos));
            this.mediaFiles.push(eachItem);  // Add to mediaFiles array
          }
        });

        this.mediaFiles = [...this.mediaFiles];
      });


    this.dailyReportService.triggerSave
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        if (this.form) {
          this.save();
        }
        if (this.videoToBeDeletedFromMuxDb.length) {
          this.videoToBeDeletedFromMuxDb.forEach(videoId => {
            this.store.dispatch(DeleteVideoFromMuxDbRequest({ payload: videoId }));
          });
        }
      });

    this.dailyReportService.triggerDelete
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        if (this.form) {
          this.delete();
        }
      });

    this.dailyReportService.selectedLocation
      .pipe(takeUntil(this.onDestroy))
      .subscribe((location) => {
        this.applyPickedLocation(location);
        this.projectsService.isAnyFormDirty.next(this.form.valid);
      });

    const initialValues = JSON.stringify(this.form.getRawValue());
    this.formSubscription = this.form.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        if (!this.editable) return;
        const changed = JSON.stringify(this.form.getRawValue()) !== initialValues;
        this.projectsService.isAnyFormDirty.next(changed && this.form.valid);
      });

    this.store.select(dailyReport)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((report: any) => {
        this.dailyReport = { ...report };
        this.editable = this.ability.can('create', subject('DailyReport', this.dailyReport));
        if (!this.editable) {
          this.form.disable({ emitEvent: false });
        } else if (!this.options?.disabled) {
          this.form.enable({ emitEvent: false });
        }
      });
  }

  save(): void {
    const { index, ...rest } = this.data;

    // A link removed this session is { sheet: null }; an untouched null stays a bare null (R54)
    const values = linkRawValue(this.form);
    const payload = {
      ...rest,
      ...values,
      pictures: JSON.parse(values.pictures),
      videos: JSON.parse(values.videos),
      map_feature_link: recordLinkPayload(values.map_feature_link, values.latitude, values.longitude),
    };

    this.dailyReportService.save.next({
      type: 'activities',
      data: [payload],
    });
  }

  delete() {
    this.dailyReportService.delete.next({
      type: 'activities',
      id: this.data.id,
    });
  }

  deletePicture(pic) {
    if (pic.fileType === 'picture') {
      let pictures = this.getPictures();

      pictures = pictures?.filter(o => o.id !== pic.id);
      this.mediaFiles = this.mediaFiles?.filter(o => o.id !== pic.id);
      this.form?.get('pictures')?.setValue(JSON.stringify(pictures));
    }

    if (pic.fileType === 'video') {
      let videos = this.getVideos();

      videos = videos?.filter(o => o.id !== pic.id);
      this.mediaFiles = this.mediaFiles?.filter(o => o.id !== pic.id);
      this.form?.get('videos')?.setValue(JSON.stringify(videos));
      this.videoToBeDeletedFromMuxDb.push(pic.id);
    }
  }

  deleteLocation() {
    clearLocationControls(this.form, ['station_range']);
  }

  /** Picker / station-dialog result: only the keys it sent are patched (an absent link key leaves the link). */
  applyPickedLocation(location: unknown) {
    patchPickedLocation(this.form, location, { emitEvent: false });
  }

  getPictures() {
    const pictures = this.form.controls['pictures']?.value || '[]';
    return JSON.parse(pictures);
  }

  getVideos() {
    const videos = this.form.controls['videos']?.value || '[]';
    return JSON.parse(videos);
  }

  showLocationSection() {
    const hasGps = this.form?.controls?.latitude?.value !== null &&
      this.form?.controls?.longitude?.value !== null &&
      this.form?.controls?.latitude?.value !== '' &&
      this.form?.controls?.longitude?.value !== '';

    // W50: a sheet-only drawing link has no pin but still shows its card
    return hasGps || !!this.form?.controls?.location?.value || !!this.form?.controls?.station_range?.value
      || !!this.form?.controls?.map_feature_link?.value?.sheet;
  }

  checkForErrors(currentField?: string) {
    this.errorMessages = {
      ...this.errorMessages,
      ...(this.commonService.checkFormValidation(this.form, this.errorMessageMap, currentField)),
    };
  }

  ngOnDestroy() {
    this.formSubscription?.unsubscribe();
    this.cdRef.detach();
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
